import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Set up the worker for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface SimplePdfViewerProps {
  pdfUrl: string;
  width?: string | number;
  height?: string | number;
  onClose?: () => void;
}

export function SimplePdfViewer({
  pdfUrl,
  width = '100%',
  height = 600,
  onClose,
}: SimplePdfViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState<number>(1.0);

  // Set a timeout to clear the loading state after 10 seconds
  // This ensures we don't get stuck in loading state if the PDF fails to load
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        setLoading(false);
        setError(
          'PDF loading timed out. Please try downloading it instead.'
        );
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [loading]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setPageNumber(1);
    setLoading(false);
  }

  function onDocumentLoadError(error: Error) {
    console.error('Error loading PDF:', error);
    setLoading(false);
    setError('Report pdf is ready now.');
  }

  function changePage(offset: number) {
    setPageNumber((prevPageNumber) => {
      const newPageNumber = prevPageNumber + offset;
      return Math.max(1, Math.min(numPages || 1, newPageNumber));
    });
  }

  function previousPage() {
    changePage(-1);
  }

  function nextPage() {
    changePage(1);
  }

  function zoomIn() {
    setScale((prevScale) => Math.min(prevScale + 0.2, 3.0));
  }

  function zoomOut() {
    setScale((prevScale) => Math.max(prevScale - 0.2, 0.5));
  }

  return (
    <div
      className="pdf-viewer relative bg-gray-100 rounded-lg overflow-hidden"
      style={{
        width,
        height: typeof height === 'number' ? `${height}px` : height,
      }}
    >
      {/* Close button */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-20 bg-white rounded-full p-1 shadow-md hover:bg-gray-100"
          aria-label="Close PDF viewer"
        >
          <X className="h-5 w-5 text-gray-600" />
        </button>
      )}

      {loading && (
        <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-80 z-10">
          <div className="h-10 w-10 border-4 border-[#b89256] border-t-transparent rounded-full animate-spin"></div>
          <p className="ml-3 text-gray-600">Loading PDF...</p>
        </div>
      )}

      <div className="flex flex-col h-full">
        {/* PDF Controls */}
        <div className="bg-white p-2 border-b flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <button
              onClick={previousPage}
              disabled={pageNumber <= 1}
              className="px-2 py-1 bg-gray-100 border border-gray-300 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span>
              Page {pageNumber} of {numPages || '--'}
            </span>
            <button
              onClick={nextPage}
              disabled={!numPages || pageNumber >= numPages}
              className="px-2 py-1 bg-gray-100 border border-gray-300 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={zoomOut}
              className="px-2 py-1 bg-gray-100 border border-gray-300 rounded"
            >
              -
            </button>
            <span>{Math.round(scale * 100)}%</span>
            <button
              onClick={zoomIn}
              className="px-2 py-1 bg-gray-100 border border-gray-300 rounded"
            >
              +
            </button>
          </div>
        </div>

        {/* PDF Document */}
        <div className="flex-1 overflow-auto p-4 flex justify-center">
          {!error ? (
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={null} // We handle loading state ourselves
              error={null} // We handle error state ourselves
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                className="shadow-lg"
              />
            </Document>
          ) : (
            <div className="flex flex-col justify-center items-center h-full bg-red-50 p-4 rounded-md">
              <p className="text-red-600 mb-2">
                {error ||
                  'Your browser cannot display this PDF. Please download it instead.'}
              </p>
              <div className="flex space-x-4 mb-4">
                <a
                  href={pdfUrl}
                  download="document.pdf"
                  className="px-4 py-2 bg-[#b89256] text-white rounded-md hover:bg-[#a17d45]"
                >
                  Download PDF
                </a>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 border border-[#b89256] text-[#b89256] rounded-md hover:bg-[#f8f3e9]"
                >
                  Open in New Tab
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
